/**
 * Como a lista de sessões se arruma: filtro, ordenação e agrupamento.
 *
 * Mora fora do componente porque é a única parte disto que dá para provar sem
 * abrir navegador — e porque a lista tem muitos projetos misturados, o que faz
 * do agrupamento a peça mais fácil de errar em silêncio.
 *
 * **Tudo aqui acontece no cliente, de propósito.** O servidor devolve uma
 * página só, com teto de `TETO_LISTA_SESSOES` linhas e sem cursor: o painel já
 * tem em mãos exatamente o conjunto que o servidor chama de "a lista", então
 * reordenar aqui não pode discordar dele sobre QUAIS conversas existem. O que
 * o teto esconde são as mais antigas — e é por isso que a lista avisa quando
 * está encostada nele, em vez de deixar "mais antigas" mentir.
 *
 * A forma mínima que o arranjo precisa de uma sessão. Declarada aqui, e não
 * importada da lista, para este módulo não depender do componente que o usa —
 * é o que deixa os testes serem três objetos literais.
 */
export interface SessaoArranjavel {
  cwd: string;
  maquinaNome: string;
  ultimoEventoEm: string | null;
}

export type AgruparPor = 'maquina' | 'projeto' | 'periodo' | 'nenhum';
export type OrdenarPor = 'recentes' | 'antigas' | 'nome';

export interface ArranjoDeSessoes {
  agrupar: AgruparPor;
  ordenar: OrdenarPor;
  /** O `cwd` exato do projeto, ou vazio para todos. */
  projeto: string;
}

/**
 * O padrão reproduz a lista de antes desta barra existir.
 *
 * Agrupada por máquina, na ordem em que o servidor mandou. Quem não escolheu
 * nada não pode ver a lista mudar de forma sozinha — a barra é uma oferta, não
 * uma reforma.
 */
export const ARRANJO_PADRAO: ArranjoDeSessoes = {
  agrupar: 'maquina',
  ordenar: 'recentes',
  projeto: '',
};

export const AGRUPAMENTOS: { id: AgruparPor; rotulo: string; descricao: string }[] = [
  { id: 'maquina', rotulo: 'Por máquina', descricao: 'onde a conversa está rodando' },
  { id: 'projeto', rotulo: 'Por projeto', descricao: 'a pasta de trabalho da conversa' },
  { id: 'periodo', rotulo: 'Por período', descricao: 'hoje, ontem, últimos dias' },
  { id: 'nenhum', rotulo: 'Sem grupos', descricao: 'uma lista corrida' },
];

export const ORDENACOES: { id: OrdenarPor; rotulo: string; descricao: string }[] = [
  { id: 'recentes', rotulo: 'Mais recentes', descricao: 'a que se mexeu por último no topo' },
  { id: 'antigas', rotulo: 'Mais antigas', descricao: 'a parada há mais tempo no topo' },
  { id: 'nome', rotulo: 'Por nome', descricao: 'de A a Z' },
];

/** Nada escolhido: a barra não tem o que oferecer para limpar. */
export function arranjoNoPadrao(arranjo: ArranjoDeSessoes): boolean {
  return (
    arranjo.agrupar === ARRANJO_PADRAO.agrupar &&
    arranjo.ordenar === ARRANJO_PADRAO.ordenar &&
    arranjo.projeto === ARRANJO_PADRAO.projeto
  );
}

/**
 * O arranjo guardado, campo a campo.
 *
 * `localStorage` é texto que qualquer coisa pode ter escrito — uma versão
 * antiga do painel, uma extensão, um dedo no console. Um `agrupar` fora da
 * lista viraria um agrupamento que não existe e deixaria a lateral vazia, que
 * é a falha mais confusa possível: a pessoa vê "nenhuma sessão" com sessões
 * abertas. Valor desconhecido volta ao padrão, um por um, sem derrubar os
 * outros dois.
 */
export function sanearArranjo(cru: unknown): ArranjoDeSessoes {
  const bruto = (cru ?? {}) as Partial<Record<keyof ArranjoDeSessoes, unknown>>;
  const agrupar = AGRUPAMENTOS.some((a) => a.id === bruto.agrupar)
    ? (bruto.agrupar as AgruparPor)
    : ARRANJO_PADRAO.agrupar;
  const ordenar = ORDENACOES.some((o) => o.id === bruto.ordenar)
    ? (bruto.ordenar as OrdenarPor)
    : ARRANJO_PADRAO.ordenar;
  const projeto = typeof bruto.projeto === 'string' ? bruto.projeto : ARRANJO_PADRAO.projeto;
  return { agrupar, ordenar, projeto };
}

/**
 * Última pasta do caminho: é assim que a pessoa reconhece o projeto.
 *
 * A definição mudou para `packages/shared/src/sessoes/titulo.ts`, junto do
 * `tituloDaSessao`, porque a API precisa da mesma coisa — o texto do push e o
 * cartão de permissão também chamam a conversa pelo nome, e nada em `apps/web`
 * pode ser importado de lá. Reexportado aqui para não mexer em quem já importa
 * `pastaCurta` deste módulo.
 */
import { pastaCurta } from '@remoto/shared';

export { pastaCurta };

/** O nome da pasta de trabalho, sem caminho nenhum: `Remoto`. */
export function nomeDoProjeto(cwd: string): string {
  const partes = cwd.split(/[\\/]/).filter(Boolean);
  return partes[partes.length - 1] ?? cwd;
}

/**
 * A identidade de um projeto — **a pasta, não o texto do caminho**.
 *
 * O `cwd` cru era a chave, e por isso o mesmo projeto virava dois grupos com
 * cabeçalhos gêmeos: `C:\...\Remoto` e `c:/.../remoto/` são o mesmo lugar
 * escrito de dois jeitos, e o Windows nem diferencia maiúscula de minúscula.
 * A tela mostra tudo em caixa alta, então a diferença ficava invisível — dava
 * para ter uma conversa perdida no grupo gêmeo sem ninguém entender por quê.
 *
 * A chave é o NOME da pasta, e não o caminho inteiro, de propósito: `APPs\Remoto`
 * no PC e `projetos/Remoto` na VPS são o mesmo projeto visto de duas máquinas, e
 * "agrupar por projeto" pergunta em que se está mexendo — não onde roda, que é o
 * que o agrupamento por máquina responde e o que cada linha já diz embaixo do
 * nome.
 */
export function chaveDoProjeto(cwd: string): string {
  return nomeDoProjeto(cwd).toLowerCase();
}

/**
 * Os projetos presentes na lista, para o filtro. Ordem alfabética.
 *
 * A chave viaja para o `localStorage`, então ela é o nome da pasta — e o
 * filtro compara chave com chave, o que faz uma escolha guardada como caminho
 * inteiro por uma versão antiga continuar valendo.
 */
export function projetosDe(sessoes: SessaoArranjavel[]): { chave: string; rotulo: string }[] {
  const porChave = new Map<string, string>();
  for (const s of sessoes) {
    const chave = chaveDoProjeto(s.cwd);
    if (!porChave.has(chave)) porChave.set(chave, nomeDoProjeto(s.cwd));
  }
  return [...porChave]
    .map(([chave, rotulo]) => ({ chave, rotulo }))
    .sort((a, b) => a.rotulo.localeCompare(b.rotulo, 'pt-BR', { sensitivity: 'base' }));
}

/**
 * O cabeçalho do grupo de projeto — que **confessa quando funde**.
 *
 * Juntar `APPs/Remoto` e `projetos/Remoto` sob "Remoto" é o que se quer: é um
 * projeto só, em duas máquinas. Mas `a/web` e `b/web` são dois projetos que por
 * acaso têm o mesmo nome de pasta, e um cabeçalho "web" seco daria a entender
 * que são a mesma coisa.
 *
 * Nos dois casos o grupo é um só, e o cabeçalho diz de quantas pastas ele é
 * feito — o que resolve sem ter de adivinhar qual dos dois casos é. Quem quiser
 * saber quais, lê nas linhas: cada uma mostra a pasta e a máquina embaixo do
 * nome. Separar em dois grupos "web" seria devolver a repetição que motivou
 * este conserto; fundir calado seria mentir.
 */
function rotuloDoProjeto(sessoes: SessaoArranjavel[]): string {
  const nome = nomeDoProjeto(sessoes[0]?.cwd ?? '');
  // A mesma pasta escrita com barra invertida e com barra normal é UMA pasta:
  // sem unificar o separador, a contagem acusaria fusão onde não houve.
  const pastas = new Set(
    sessoes.map((s) =>
      s.cwd
        .replace(/\\/g, '/')
        .replace(/\/+$/, '')
        .toLowerCase(),
    ),
  );
  return pastas.size > 1 ? `${nome} · ${pastas.size} pastas` : nome;
}

export type ChaveDePeriodo = 'hoje' | 'ontem' | 'semana' | 'mes' | 'antigas' | 'sem';

/**
 * Os períodos na ordem em que a tela os mostra — sempre esta, e não a ordem em
 * que apareceram na lista, que "por nome" embaralharia.
 */
const PERIODOS: { id: ChaveDePeriodo; rotulo: string }[] = [
  { id: 'hoje', rotulo: 'Hoje' },
  { id: 'ontem', rotulo: 'Ontem' },
  { id: 'semana', rotulo: 'Últimos 7 dias' },
  { id: 'mes', rotulo: 'Últimos 30 dias' },
  { id: 'antigas', rotulo: 'Mais antigas' },
  { id: 'sem', rotulo: 'Sem atividade' },
];

const DIA = 24 * 60 * 60 * 1000;

/**
 * Em que faixa do calendário esta conversa cai.
 *
 * As bordas são de **dia do calendário local**, não "24 horas atrás": às 9h da
 * manhã, uma conversa das 23h de ontem tem dez horas de idade, mas a pessoa a
 * procura em "Ontem" — que é onde ela esteve.
 *
 * **Sem `ultimoEventoEm` cai em "Sem atividade", no fim.** A lista tem muitas
 * assim, e as duas alternativas eram piores: escondê-las faria conversas
 * abertas sumirem da tela, e cair em "Hoje" pelo `criadoEm` misturaria o que
 * respondeu agora com o que nunca respondeu — que é justamente a diferença que
 * se olha. Data ilegível recebe o mesmo tratamento, pelo mesmo motivo: sumir
 * calada é o único desfecho proibido.
 */
export function periodoDe(ultimoEventoEm: string | null, agora: number): ChaveDePeriodo {
  if (!ultimoEventoEm) return 'sem';
  const quando = Date.parse(ultimoEventoEm);
  if (Number.isNaN(quando)) return 'sem';
  const inicioDeHoje = new Date(agora).setHours(0, 0, 0, 0);
  if (quando >= inicioDeHoje) return 'hoje';
  if (quando >= inicioDeHoje - DIA) return 'ontem';
  if (quando >= inicioDeHoje - 7 * DIA) return 'semana';
  if (quando >= inicioDeHoje - 30 * DIA) return 'mes';
  return 'antigas';
}

/**
 * Ordena — e "Mais recentes" **não mexe em nada**.
 *
 * O servidor já devolve por `ultimoEventoEm desc, criadoEm desc`. Refazer essa
 * conta aqui daria um resultado quase igual e sutilmente diferente: no
 * Postgres, `DESC` põe os nulos ANTES, então a conversa recém-criada que ainda
 * não teve evento chega no topo — e um comparador nosso, escrito sem pensar
 * nisso, a jogaria para o fim. Como "sem escolha nenhuma" precisa reproduzir a
 * lista de hoje exatamente, o caminho padrão é a identidade.
 *
 * Pela mesma razão "Mais antigas" é o inverso da lista recebida, e não um
 * comparador novo: assim ela é, por construção, o espelho fiel da ordem do
 * servidor — nulos no fim, e nenhuma regra de desempate para manter em dois
 * lugares.
 */
export function ordenarSessoes<T extends SessaoArranjavel>(
  sessoes: T[],
  ordem: OrdenarPor,
  nomeDe: (sessao: T) => string,
): T[] {
  if (ordem === 'recentes') return sessoes;
  if (ordem === 'antigas') return [...sessoes].reverse();
  // `sort` é estável: empate de nome preserva a ordem do servidor.
  return [...sessoes].sort((a, b) =>
    nomeDe(a).localeCompare(nomeDe(b), 'pt-BR', { sensitivity: 'base' }),
  );
}

export interface GrupoDeSessoes<T> {
  /** Estável e única — serve de `key` do React. */
  chave: string;
  /** O cabeçalho da seção. Vazio quando não há grupos. */
  rotulo: string;
  sessoes: T[];
}

/**
 * Reparte a lista em seções.
 *
 * **Grupo vazio nunca nasce**: as seções saem do que está na lista, não de uma
 * tabela de seções possíveis. Máquina e projeto ficam na ordem em que
 * apareceram, o que herda a ordenação escolhida — a máquina com a conversa mais
 * recente encabeça a lista sem precisar de uma segunda regra de ordenação.
 */
export function agruparSessoes<T extends SessaoArranjavel>(
  sessoes: T[],
  por: AgruparPor,
  agora: number,
): GrupoDeSessoes<T>[] {
  if (por === 'nenhum') {
    return sessoes.length === 0 ? [] : [{ chave: 'todas', rotulo: '', sessoes }];
  }

  if (por === 'periodo') {
    const baldes = new Map<ChaveDePeriodo, T[]>();
    for (const sessao of sessoes) {
      const faixa = periodoDe(sessao.ultimoEventoEm, agora);
      baldes.set(faixa, [...(baldes.get(faixa) ?? []), sessao]);
    }
    return PERIODOS.filter((p) => baldes.has(p.id)).map((p) => ({
      chave: p.id,
      rotulo: p.rotulo,
      sessoes: baldes.get(p.id) as T[],
    }));
  }

  const chaveDe = (sessao: T): string =>
    por === 'projeto' ? chaveDoProjeto(sessao.cwd) : sessao.maquinaNome;
  const grupos = new Map<string, T[]>();
  for (const sessao of sessoes) {
    const chave = chaveDe(sessao);
    grupos.set(chave, [...(grupos.get(chave) ?? []), sessao]);
  }
  return [...grupos].map(([chave, lista]) => ({
    chave,
    rotulo: por === 'projeto' ? rotuloDoProjeto(lista) : chave,
    sessoes: lista,
  }));
}

/** O que o arranjo precisa saber para guardar sessões dentro da mestre. */
export interface SessaoDaMestre {
  id: string;
  estado: string;
  funcao?: string;
  motor?: string;
  ultimoEventoEm: string | null;
  /** Ids das mestres vigentes que vigiam esta conversa (a API resolve a herança). */
  vigiadaPor?: string[];
}

export interface FilhasDaMestre<T> {
  /** Conversas que esta mestre vigia. */
  vigiadas: T[];
  /** Mestres encerradas do MESMO motor, a mais recente primeiro. */
  anteriores: T[];
}

const motorDe = (s: { motor?: string }): string => s.motor ?? 'CLAUDE';

/**
 * Tira da lista o que mora DENTRO de cada mestre.
 *
 * Dois grupos, e os dois saem dos grupos normais: as conversas que a mestre
 * vigia, e as mestres anteriores (encerradas, do mesmo motor — cada "recomeçar"
 * encerra a vigente e cria outra). Vigiada de uma mestre que não está em
 * `mestres` fica no `resto`: sem a mestre na tela, esconder a conversa dentro
 * dela seria fazê-la sumir. O que sobra em `resto` mantém a ordem recebida.
 */
export function separarDaMestre<T extends SessaoDaMestre>(
  sessoes: readonly T[],
  mestres: readonly T[],
): { porMestre: Map<string, FilhasDaMestre<T>>; resto: T[] } {
  const porMestre = new Map<string, FilhasDaMestre<T>>(
    mestres.map((m) => [m.id, { vigiadas: [], anteriores: [] }]),
  );
  const resto: T[] = [];
  for (const s of sessoes) {
    if (s.funcao === 'MESTRE') {
      const dona =
        s.estado === 'ENCERRADA' ? mestres.find((m) => motorDe(m) === motorDe(s)) : undefined;
      if (dona) porMestre.get(dona.id)?.anteriores.push(s);
      else resto.push(s);
      continue;
    }
    const dona = mestres.find((m) => s.vigiadaPor?.includes(m.id));
    if (dona) porMestre.get(dona.id)?.vigiadas.push(s);
    else resto.push(s);
  }
  const maisRecenteDepois = (a: T, b: T): number =>
    (Date.parse(b.ultimoEventoEm ?? '') || 0) - (Date.parse(a.ultimoEventoEm ?? '') || 0);
  for (const filhas of porMestre.values()) filhas.anteriores.sort(maisRecenteDepois);
  return { porMestre, resto };
}

/**
 * A busca da lista olhando DENTRO das mestres.
 *
 * `separarDaMestre` tira vigiadas e anteriores do `resto` antes do filtro de
 * texto, então sem isto a busca nunca as acharia. Devolve só as mestres que
 * têm alguma filha batendo, com só as que batem; `bate` é o MESMO predicado
 * que filtra o resto. Busca vazia devolve o mapa como veio.
 */
export function filtrarFilhasDaMestre<T>(
  porMestre: ReadonlyMap<string, FilhasDaMestre<T>>,
  busca: string,
  bate: (sessao: T) => boolean,
): ReadonlyMap<string, FilhasDaMestre<T>> {
  if (!busca.trim()) return porMestre;
  const achadas = new Map<string, FilhasDaMestre<T>>();
  for (const [id, filhas] of porMestre) {
    const vigiadas = filhas.vigiadas.filter(bate);
    const anteriores = filhas.anteriores.filter(bate);
    if (vigiadas.length + anteriores.length > 0) achadas.set(id, { vigiadas, anteriores });
  }
  return achadas;
}
